// =====================================================================
// ErrorFormModal.jsx — Modal nouvelle/édition erreur (M-006)
// =====================================================================
import { useState, useEffect } from 'react';
import { errors as errorsApi } from '../../services/api.js';
import { marqueurInactif } from '../../lib/tri-personnes.js';
import { aujourdhuiBruxelles } from '../../lib/dates.js';

function todayYmd() { return aujourdhuiBruxelles(); }   // date du jour à Bruxelles (lib/dates.js)

export default function ErrorFormModal({ editing, typologies, users, projects, onSaved, onClose }) {
  const isEdit = !!editing;
  const [form, setForm] = useState(() => ({
    date: editing?.date || todayYmd(),
    movementRef: editing?.movementRef || '',
    operatorUserId: editing?.operatorUserId || '',
    projectId: editing?.projectId || '',
    typologyId: editing?.typologyId || '',
    explanation: editing?.explanation || '',
    depotComment: editing?.depotComment || '',
    cost: editing?.cost != null ? String(editing.cost) : '',
    reorderRef: editing?.reorderRef || '',
    brainResponsibility: editing?.brainResponsibility || 'YES',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Group typologies by category for optgroup display
  const grouped = typologies.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  function update(field, value) {
    setForm(s => ({ ...s, [field]: value }));
  }

  async function handleSubmit() {
    setError(null);
    // Validation client
    if (!form.date || !form.movementRef.trim() || !form.operatorUserId || !form.projectId || !form.typologyId || !form.explanation.trim()) {
      setError('Tous les champs marqués * sont obligatoires');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        movementRef: form.movementRef.trim(),
        operatorUserId: form.operatorUserId,
        projectId: form.projectId,
        typologyId: form.typologyId,
        explanation: form.explanation.trim(),
        depotComment: form.depotComment.trim() || null,
        cost: form.cost ? parseFloat(form.cost) : null,
        reorderRef: form.reorderRef.trim() || null,
        brainResponsibility: form.brainResponsibility,
      };
      if (isEdit) {
        await errorsApi.update(editing.id, payload);
      } else {
        await errorsApi.create(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
    }}>
      <div className="modal-mobile-fullscreen" onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: 24, maxWidth: 720, width: '100%',
        maxHeight: '90vh', overflow: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>{isEdit ? '✏️ Modifier l\'erreur' : '+ Nouvelle erreur'}</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9ca3af' }}>×</button>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', color: '#b91c1c', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13, border: '1px solid #fecaca' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label="Date *">
            <input type="date" value={form.date} onChange={e => update('date', e.target.value)} style={inputStyle} disabled={saving} />
          </Field>
          <Field label="N° de mouvement *">
            <input type="text" placeholder="Ex: BROUT65664 / SDT4741 / CDE..." value={form.movementRef}
              onChange={e => update('movementRef', e.target.value)} style={inputStyle} disabled={saving} maxLength={100} />
          </Field>
          <Field label="Opérateur *">
            <select value={form.operatorUserId} onChange={e => update('operatorUserId', e.target.value)} style={inputStyle} disabled={saving}>
              <option value="">— Sélectionner —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.username}){marqueurInactif(u)}</option>)}
            </select>
          </Field>
          <Field label="Client (projet) *">
            <select value={form.projectId} onChange={e => update('projectId', e.target.value)} style={inputStyle} disabled={saving}>
              <option value="">— Sélectionner —</option>
              {/* Projets actifs, plus le projet ACTUEL de l'erreur s'il a été archivé
                  depuis : montré (sinon le champ paraît vide alors qu'il est plein),
                  marqué ; on peut y revenir après avoir changé d'avis. « Archivé » vient du
                  projet lui-même, jamais de son absence dans la liste (une liste qui
                  n'a pas pu se charger ne doit pas faire dire « archivé » à tort). */}
              {projects.filter(p => p.isActive !== false || p.id === editing?.projectId)
                .map(p => <option key={p.id} value={p.id}>{p.name}{p.isActive === false ? ' (archivé)' : ''}</option>)}
              {editing?.project && !projects.some(p => p.id === editing.project.id) && (
                <option value={editing.project.id}>{editing.project.name}</option>
              )}
            </select>
          </Field>
          <Field label="Typologie d'erreur *" wide>
            <select value={form.typologyId} onChange={e => update('typologyId', e.target.value)} style={inputStyle} disabled={saving}>
              <option value="">— Sélectionner —</option>
              {Object.entries(grouped).map(([cat, list]) => (
                <optgroup key={cat} label={catLabel(cat)}>
                  {list.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </optgroup>
              ))}
            </select>
          </Field>
          <Field label="Commentaires explicatifs *" wide>
            <textarea rows={2} placeholder="Ex: Reçu 26 KIDYMIC-BU au lieu de 26 KIDYMIC-PI..."
              value={form.explanation} onChange={e => update('explanation', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} disabled={saving} maxLength={2000} />
          </Field>
          <Field label="Commentaires dépôt (actions correctives)" wide>
            <textarea rows={2} placeholder="Ex: 1 enlèvement DPD avec relivraison"
              value={form.depotComment} onChange={e => update('depotComment', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} disabled={saving} maxLength={2000} />
          </Field>
          <Field label="Coût (€) — optionnel">
            <input type="number" min="0" step="0.01" placeholder="0.00" value={form.cost}
              onChange={e => update('cost', e.target.value)} style={inputStyle} disabled={saving} />
          </Field>
          <Field label="Nouvel ODP (si ré-expédition)">
            <input type="text" placeholder="Ex: ODP-4387102" value={form.reorderRef}
              onChange={e => update('reorderRef', e.target.value)} style={inputStyle} disabled={saving} maxLength={100} />
          </Field>
          <Field label="Responsabilité Brain E-Log *" wide>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { v: 'YES', label: 'Oui', bg: '#dc2626' },
                { v: 'NO', label: 'Non', bg: '#16a34a' },
                { v: 'TBD', label: 'À déterminer', bg: '#ca8a04' },
              ].map(opt => (
                <button key={opt.v} type="button"
                  onClick={() => update('brainResponsibility', opt.v)}
                  disabled={saving}
                  style={{
                    flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    border: form.brainResponsibility === opt.v ? `2px solid ${opt.bg}` : '1px solid #d1d5db',
                    background: form.brainResponsibility === opt.v ? opt.bg : '#fff',
                    color: form.brainResponsibility === opt.v ? '#fff' : '#374151',
                  }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </Field>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} disabled={saving} style={cancelBtnStyle}>Annuler</button>
          <button onClick={handleSubmit} disabled={saving} style={primaryBtnStyle}>
            {saving ? 'Envoi…' : (isEdit ? '✓ Enregistrer' : '+ Créer l\'erreur')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, wide, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: wide ? '1 / -1' : undefined }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
const cancelBtnStyle = { padding: '8px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13 };
const primaryBtnStyle = { padding: '8px 16px', background: '#4338ca', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 };

function catLabel(cat) {
  const labels = {
    PREPARATION: 'Préparation',
    ENCODAGE: 'Encodage',
    PROCEDURE: 'Procédure',
    RECEPTION: 'Réception',
    TRI: 'Tri',
    INVENTAIRE: 'Inventaire',
    ADMIN: 'Administratif',
    SECURITE: 'Sécurité',
    REGLEMENT: 'Règlement',
  };
  return labels[cat] || cat;
}
